<template>
  <v-dialog
    v-model="show"
    max-width="1000px"
    scrollable
    @keydown.enter="confirm"
    @keydown.esc="show = false"
    :fullscreen="mobile"
  >
    <v-card v-if="show" outlined>
      <v-card-title class="pt-0" style="justify-content: center; height: 70px">{{ modalTitle }}</v-card-title>
      <v-card-text class="pb-0 pt-1 pa-2">
        <v-form ref="form">
          <v-container>
            <v-row v-for="filter in filters" v-bind:key="filter.name">
              <v-col class="pt-1 pb-1" v-if="filter.type == 'locationSuggestion'">
                <o-location-suggestion-input
                  v-model="filter.value"
                  :label="filter.text"
                  :loading="loadingData"
                  :extra-items="fieldsByName[filter.name]?.extraItems ?? []"
                  :manual-input="fieldsByName[filter.name]?.manualInput !== false"
                  :location-ids="fieldsByName[filter.name]?.locationIds ?? null"
                  multiple
                  clearable
                  outlined
                />
              </v-col>
              <v-col class="pt-1 pb-1" v-else-if="filter.type == 'array'">
                <v-autocomplete
                  :items="filter.arrayValues"
                  :label="filter.text"
                  v-model="filter.value"
                  :multiple="filter.multiple"
                  :clearable="filter.clearable"
                  required
                  outlined
                  dense
                  hide-details
                  :search-input.sync="searchInput[filter.name]"
                  @change="searchInput[filter.name] = ''"
                >
                  <template v-slot:selection="{ item, index }">
                    <v-chip small v-if="index === 0">
                      <span v-if="index === 0">{{ item.text }}</span>
                    </v-chip>
                    <span v-if="index === 1" class="grey--text caption">
                      (+{{ filter.value.length - 1 }} {{ $t('components.select.others') }})
                    </span>
                  </template>
                </v-autocomplete>
              </v-col>
              <v-col v-else-if="filter.name === 'product_id'">
                <v-row>
                  <v-col cols="12" sm="12" class="pt-1 pb-1">
                    <v-card outlined v-if="computedCatalogAccess">
                      <o-catalog-search
                        v-model="filter.value"
                        @update:emitSelectedItems="setSelectedItems"
                        :selected-items.sync="selectedItems"
                        :outlined="true"
                        :reset-key="resetKey"
                      />
                    </v-card>
                    <v-combobox
                      v-else
                      v-model="filter.value"
                      clearable
                      :label="$t('components.filterDialog.productFilterLabel')"
                      :placeholder="$t('components.filterDialog.productFilterPlaceHolder')"
                      multiple
                      dense
                      outlined
                      hide-details="auto"
                      :error-messages="comboboxError"
                      :search-input.sync="comboboxSearch"
                      @change="onComboboxChange($event, filter)"
                      @keydown.enter.native.capture="onComboboxEnter($event, filter)"
                    >
                      <template #selection="{ attrs, item, select, selected }">
                        <v-chip
                          v-bind="attrs"
                          :input-value="selected"
                          close
                          small
                          color="primary"
                          @click="select"
                          @click:close="filter.value.splice(filter.value.indexOf(item), 1)"
                        >
                          <strong>{{ item }}</strong>
                        </v-chip>
                      </template>
                    </v-combobox>
                  </v-col>
                </v-row>
              </v-col>
              <v-col class="pt-1 pb-1" v-else-if="filter.name == 'customer'">
                <v-row>
                  <v-col cols="12" sm="6">
                    <v-text-field
                      outlined
                      dense
                      hide-details
                      v-model="filter.value['first_name']"
                      :label="$t('subOrder.firstName')"
                      clearable
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" sm="6" class="pt-0 pt-sm-3 pt-md-3 pt-lg-3 pt-xl-3">
                    <v-text-field
                      outlined
                      dense
                      hide-details
                      v-model="filter.value['last_name']"
                      :label="$t('subOrder.lastName')"
                      clearable
                    ></v-text-field>
                  </v-col>
                </v-row>
              </v-col>
              <v-col class="pt-1 pb-1" v-else-if="filter.name === 'email'">
                <v-row>
                  <v-col>
                    <v-text-field
                      outlined
                      dense
                      hide-details
                      v-model="filter.value['email']"
                      :label="$t('subOrder.email')"
                      clearable
                    ></v-text-field>
                  </v-col>
                </v-row>
              </v-col>
              <v-col class="pt-1 pb-1" v-else-if="filter.type == 'text'">
                <v-row>
                  <v-col>
                    <v-text-field
                      outlined
                      dense
                      v-model="filter.value"
                      :label="filter.text"
                      hide-details
                      clearable
                    ></v-text-field>
                  </v-col>
                </v-row>
              </v-col>
              <v-col class="pt-1 pb-1" v-else-if="filter.type == 'date'">
                <o-date-range-filter v-model="filter.value" :label="filter.text" :loading="loadingData" />
              </v-col>
              <v-col class="pt-1 pb-1" v-else-if="filter.type == 'amountRange'">
                <o-amount-range-filter v-model="filter.value" :label="filter.text" :loading="loadingData" />
              </v-col>
              <v-col class="pt-1 pb-1" v-else-if="filter.type == 'range'">
                <v-row>
                  <v-col cols="12" sm="6">
                    <v-text-field
                      outlined
                      dense
                      v-model="filter.value['min']"
                      type="number"
                      :label="$t('subOrder.min')"
                      hide-details
                      clearable
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" sm="6" class="pt-0 pt-sm-3 pt-md-3 pt-lg-3 pt-xl-3">
                    <v-text-field
                      outlined
                      dense
                      v-model="filter.value['max']"
                      type="number"
                      :label="$t('subOrder.max')"
                      hide-details
                      clearable
                    ></v-text-field>
                  </v-col>
                </v-row>
              </v-col>
              <v-col class="pt-1 pb-1" v-else-if="filter.type == 'countries'">
                <v-row>
                  <v-col>
                    <CountriesAutocomplete
                      v-model="filter.value"
                      :clearable="filter.clearable"
                      :outlined="true"
                      :dense="true"
                      :multiple="filter.multiple"
                      :chips="filter.chips"
                      item-text="name"
                    />
                  </v-col>
                </v-row>
              </v-col>
              <v-col class="pt-1 pb-1 pl-0" v-else-if="filter.type == 'checkbox'">
                <v-row>
                  <v-col class="pt-0 pb-0">
                    <v-col cols="12" class="pt-0 pb-0">
                      <v-checkbox v-model="filter.value" :label="filter.text"></v-checkbox>
                    </v-col>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
          </v-container>
        </v-form>
      </v-card-text>
      <v-card-actions style="justify-content: center; height: 70px">
        <v-row class="d-flex flex-row justify-center ma-1">
          <v-btn color="primary" outlined class="mr-4" @click="confirm()" :disabled="loadingData">
            {{ $t('subOrder.validate') }}
          </v-btn>
          <v-btn color="primary" outlined class="mr-4" @click="show = false">{{ $t('subOrder.close') }}</v-btn>
          <v-btn color="primary" outlined @click="reset" @keydown.enter="confirm">
            <v-icon>mdi-refresh</v-icon>
          </v-btn>
        </v-row>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
<script>
  import CountriesAutocomplete from '@/components/CountriesAutocomplete'
  import ODateRangeFilter from './inputs/ODateRangeFilter.vue'
  import OAmountRangeFilter from './inputs/OAmountRangeFilter.vue'
  import OLocationSuggestionInput from './inputs/OLocationSuggestionInput.vue'
  import { mapState } from 'vuex'

  export default {
    name: 'FilterDialog',
    components: {
      CountriesAutocomplete,
      ODateRangeFilter,
      OAmountRangeFilter,
      OLocationSuggestionInput,
    },
    data() {
      return {
        key: Date.now(),
        key2: Date.now() + 1,
        menu: false,
        menu2: false,
        filters: [],
        searchInput: {},
        selectedItems: [],
        resetKey: 0,
        comboboxSearch: '',
        pendingProductId: '',
        comboboxError: '',
      }
    },
    props: {
      title: {
        type: String,
        default: undefined,
      },
      value: {
        type: Boolean,
        default: false,
      },
      // Each entry of `fields` describes one filter and may carry, besides `value`/`text`:
      // - `filterType`, `multiple`, `clearable`, `chips`: read by buildForm() to shape the filter.
      // - `initialValue`: the value a filter starts with (and is reset to on `value` watcher re-open).
      // - `defaultValue` (optional): the value restored by reset() when the user clicks
      //   "Reset", instead of leaving the filter empty. Omit it to keep the previous behavior
      //   (reset clears the filter). Looked up via `fieldsByName` and deep-cloned before reuse.
      fields: {
        type: Array,
        required: true,
      },
      loadingData: {
        type: Boolean,
        default: false,
      },
    },
    mounted: function () {
      this.filters = this.buildForm()
    },
    watch: {
      comboboxSearch(val) {
        this.comboboxError = ''
        if (val && val.trim()) {
          this.pendingProductId = val.trim()
        }
      },
      value(newVal) {
        if (newVal) {
          for (const header of this.fields) {
            if (header.initialValue !== undefined) {
              const filter = this.filters.find((f) => f.name === header.value)
              if (filter) filter.value = header.initialValue || undefined
            }
          }
        }
      },
    },
    computed: {
      ...mapState(['partnerSettings']),
      // The filters are built once on mount and then hold what the user types, so
      // anything a caller can update afterwards has to be read from `fields` at
      // render time instead of being copied into them. Locations suggested on top
      // of the API ones, for instance, reach their filter only once the partner
      // settings answer, well after this dialog is mounted.
      fieldsByName() {
        return Object.fromEntries(this.fields.map((field) => [field.value, field]))
      },
      show: {
        get() {
          return this.value
        },
        set(value) {
          this.$emit('input', value)
        },
      },
      modalTitle: function () {
        if (this.title) {
          return this.title
        } else return this.$t('subOrder.toFilter')
      },
      locale() {
        return this.$i18n.locale
      },
      computedCatalogAccess() {
        return this.partnerSettings.octipas_catalog_access?.enabled
      },
      mobile() {
        return this.$vuetify.breakpoint.xsOnly
      },
    },
    methods: {
      confirm() {
        // No-catalog path only: auto-commit the text typed in the combobox but not yet
        // validated (Enter). Here the input is unambiguously the filter value, so applying
        // it on confirm avoids silent data loss. This intentionally differs from the
        // catalog path (OCatalogSearch), where the query is ambiguous (search-to-browse vs
        // literal ref) and must be validated explicitly via its button or Enter.
        if (this.pendingProductId) {
          const productFilter = this.filters.find((f) => f.name === 'product_id')
          if (productFilter) {
            if (!Array.isArray(productFilter.value)) productFilter.value = []
            if (!productFilter.value.includes(this.pendingProductId)) {
              productFilter.value = [...productFilter.value, this.pendingProductId]
            }
          }
          this.pendingProductId = ''
        }
        this.$nextTick(() => {
          for (let filter of this.filters) {
            filter.active = this.filterHasValue(filter.value)
          }
          this.$emit('confirm', structuredClone(this.filters))
          this.show = false
        })
      },
      buildForm: function () {
        let filters = []
        for (let header of this.fields.filter((header) => header.filterType !== undefined)) {
          let filter = {
            text: header?.text || '',
            name: header.value,
            type: header.filterType,
            multiple: header.multiple !== undefined ? header.multiple : true,
            clearable: header.clearable !== undefined ? header.clearable : false,
            chips: header.chips !== undefined ? header.chips : false,
          }
          if (header.filterType !== 'array') {
            if (header.value === 'product_id' || header.filterType === 'locationSuggestion') {
              filter.value = []
            } else if (
              header.filterType === 'date' ||
              header.value === 'customer' ||
              header.filterType === 'range' ||
              header.filterType === 'amountRange' ||
              header.value === 'email'
            ) {
              filter.value = {}
            } else {
              filter.value = header.initialValue !== undefined ? header.initialValue : undefined
            }
            filters.push(filter)
          } else if (header.arrayValues.length > 0) {
            filter.arrayValues = header.arrayValues
            // Declared here, like the branch above, rather than left to the `value` watcher or to
            // reset(): Vue 2 only turns the properties an object already owns into reactive
            // accessors, so a `value` created after `filters` was assigned would update the data
            // without the bound input ever hearing about it.
            filter.value = header.initialValue !== undefined ? header.initialValue : undefined
            filters.push(filter)
          }
        }
        return filters
      },
      // "Reset" must restore the configured default filter, not a blank one. Vuetify's
      // v-form reset() clears every bound `filter.value` asynchronously (children update on the
      // next tick), so reapplying defaults synchronously here would just be wiped out right after;
      // hence the $nextTick. Each `defaultValue` is deep-cloned before assignment so it stays the
      // object declared on the parent's `fields` prop, unaffected by later edits made in this form.
      reset: function () {
        this.$refs.form.reset()

        this.selectedItems = []
        this.resetKey++

        this.$nextTick(() => {
          for (const filter of this.filters) {
            const field = this.fieldsByName[filter.name]
            if (field?.defaultValue === undefined) {
              continue
            }
            filter.value = structuredClone(field.defaultValue)
          }
        })
      },
      filterHasValue(filterValue) {
        if (filterValue == undefined) {
          return false
        } else if (Array.isArray(filterValue)) {
          if (filterValue.length === 0) {
            return false
          } else {
            return filterValue
          }
        } else if (typeof filterValue === 'object') {
          if (Object.keys(filterValue).length === 0) {
            return false
          } else {
            return Object.values(filterValue).some((value) => this.filterHasValue(value))
          }
        } else if (typeof filterValue === 'string' && filterValue.trim() === '') {
          return false
        } else return !(typeof filterValue === 'boolean' && !filterValue)
      },
      setSelectedItems(items) {
        this.selectedItems = items
      },
      onComboboxChange(event, filter) {
        this.pendingProductId = ''
        this.comboboxSearch = ''
        this.comboboxError = ''
        if (Array.isArray(filter.value)) {
          filter.value = filter.value.map((item) => (typeof item === 'string' ? item.trim() : item))
        }
      },
      // Enter in the combobox is caught in the capture phase. With an empty input we
      // let it through so it bubbles to the dialog and validates the modal. With a
      // value we stop it (no modal validation, no native add) and handle it ourselves:
      // flag a value already in the list, otherwise add it.
      onComboboxEnter(event, filter) {
        const trimmedValue = this.comboboxSearch?.trim()
        if (!trimmedValue) {
          return
        }
        event.stopPropagation()
        event.preventDefault()
        const current = Array.isArray(filter.value) ? filter.value : []
        if (current.includes(trimmedValue)) {
          this.comboboxError = this.$t('components.filterDialog.alreadyInList', { value: trimmedValue })
          return
        }
        filter.value = [...current, trimmedValue]
        this.comboboxError = ''
        this.comboboxSearch = ''
        this.pendingProductId = ''
      },
    },
  }
</script>
<style scoped>
  .w-45 {
    width: 45%;
  }
</style>
